/**
 * Chunk → offscreen-bitmap cache — the web analog of the engine's
 * TilemapRenderer::SyncChunk (engine 45.4, "one mesh per chunk, rebuilt on
 * dirty"). Each painted chunk is rasterized ONCE into a native-tile-resolution
 * canvas and thereafter blitted as a single image; a chunk is re-rasterized
 * only when its `renderDirty` flag is set (editing one tile dirties one chunk).
 *
 * Native-resolution bitmaps + nearest-neighbor sampling + integer-aligned
 * blits are the Canvas2D equivalent of the engine's no-mips / half-texel-inset
 * anti-bleed rules.
 */

import { CHUNK_SIZE, EMPTY_TILE, type TileID } from '../model/constants';
import type { Chunk } from '../model/chunk';
import type { TilesetImage } from './tileset';
import { tileAnimationFor } from '../model/tileMeta';

/** A cell whose tile has an animation (_docs/tilemap/07 roadmap) — skipped in
 * the static bitmap and drawn by the surface's per-frame overlay pass instead. */
export interface AnimatedCell {
    localX: number;
    localY: number;
    tileId: TileID;
}

interface Cached {
    canvas: HTMLCanvasElement;
    /** Guards against a stale bitmap when a tileset re-slice changes geometry. */
    tilesetVersion: string;
    animatedCells: AnimatedCell[];
}

export class ChunkRenderer {
    private cache = new Map<string, Cached>();

    /** LRU-ish cap; evicted chunks simply re-rasterize on next visibility. */
    constructor(private readonly cap = 512) {}

    private key(layerUuid: string, cx: number, cy: number): string {
        return `${layerUuid}:${cx},${cy}`;
    }

    /**
     * Return the cached bitmap for a chunk, rasterizing (or re-rasterizing) it
     * if dirty or missing. Clears the chunk's `renderDirty` flag on rebuild.
     */
    bitmapFor(layerUuid: string, chunk: Chunk, tileset: TilesetImage): HTMLCanvasElement {
        const key = this.key(layerUuid, chunk.cx, chunk.cy);
        const tsVersion = `${tileset.meta.uuid}:${tileset.meta.tile_width}x${tileset.meta.tile_height}:${tileset.columns}`;
        const cached = this.cache.get(key);

        if (cached && !chunk.renderDirty && cached.tilesetVersion === tsVersion) {
            return cached.canvas;
        }

        const tw = tileset.meta.tile_width;
        const th = tileset.meta.tile_height;
        const canvas = cached?.canvas ?? document.createElement('canvas');
        canvas.width = CHUNK_SIZE * tw;
        canvas.height = CHUNK_SIZE * th;

        const ctx = canvas.getContext('2d')!;
        ctx.imageSmoothingEnabled = false; // nearest-neighbor (engine 45.1)
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        const animatedCells: AnimatedCell[] = [];
        for (let ty = 0; ty < CHUNK_SIZE; ty++) {
            for (let tx = 0; tx < CHUNK_SIZE; tx++) {
                const id = chunk.tiles[ty * CHUNK_SIZE + tx];
                if (id === EMPTY_TILE) continue;

                if (tileAnimationFor(tileset.meta, id)) {
                    // Animated tiles are left transparent in the static bitmap —
                    // the surface's per-frame overlay pass draws the current
                    // frame for exactly these cells, every tick.
                    animatedCells.push({ localX: tx, localY: ty, tileId: id });
                    continue;
                }

                const src = tileset.tileRectPx(id - 1);
                // y-up chunk rows → y-down bitmap rows (row flip): the bottom
                // tile row of the chunk draws at the bottom of the bitmap.
                const dy = (CHUNK_SIZE - 1 - ty) * th;
                ctx.drawImage(tileset.image, src.x, src.y, src.w, src.h, tx * tw, dy, tw, th);
            }
        }

        chunk.renderDirty = false;
        this.cache.set(key, { canvas, tilesetVersion: tsVersion, animatedCells });
        this.evictIfNeeded();
        return canvas;
    }

    /**
     * The chunk's animated cells (empty if none, or if the chunk hasn't been
     * baked yet — call `bitmapFor` first). Kept in sync with the bitmap cache:
     * both are recomputed together, whenever `renderDirty` requires a rebake.
     */
    animatedCellsFor(layerUuid: string, cx: number, cy: number): AnimatedCell[] {
        return this.cache.get(this.key(layerUuid, cx, cy))?.animatedCells ?? [];
    }

    /** Drop a chunk's cached bitmap (e.g. when it's emptied/deleted). */
    invalidate(layerUuid: string, cx: number, cy: number): void {
        this.cache.delete(this.key(layerUuid, cx, cy));
    }

    /** Drop everything (document close / hard reload). */
    clear(): void {
        this.cache.clear();
    }

    private evictIfNeeded(): void {
        if (this.cache.size <= this.cap) return;
        // Map preserves insertion order; drop the oldest entries.
        const overflow = this.cache.size - this.cap;
        let i = 0;
        for (const k of this.cache.keys()) {
            if (i++ >= overflow) break;
            this.cache.delete(k);
        }
    }
}
